/**
 * TICKET-4 — IKnowBadge
 *
 * Renders "I Know" (evidence present) or "I Think" (assumption, no evidence).
 *
 * State "i_know":  bg-emerald-100 text-emerald-800 border-emerald-300 + lock icon
 * State "i_think": bg-neutral-100 text-neutral-500 border-neutral-300 + question-mark icon
 *
 * Brand: ink #0B0B0F, red #DC2626, cream #FAFAF7 — DM Sans/Inter
 * No exclamation points, no emojis.
 */

interface IKnowBadgeProps {
  /** "i_know" = evidence present; "i_think" = assumption/no evidence */
  state: "i_know" | "i_think";
  /** Optional: size variant */
  size?: "sm" | "md";
  /** Optional: show tooltip with evidence type label */
  evidenceType?: "text" | "url" | "file" | null;
  className?: string;
}

const EVIDENCE_TYPE_LABEL: Record<string, string> = {
  text: "Text note",
  url: "URL link",
  file: "File upload",
};

export default function IKnowBadge({
  state,
  size = "sm",
  evidenceType,
  className = "",
}: IKnowBadgeProps) {
  const isKnow = state === "i_know";
  const label = isKnow ? "I Know" : "I Think";

  const sizeClass = size === "sm"
    ? "text-[10px] px-1.5 py-0.5 gap-0.5"
    : "text-xs px-2 py-1 gap-1";

  const colorClass = isKnow
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : "bg-neutral-100 text-neutral-500 border-neutral-300";

  const title = isKnow
    ? evidenceType
      ? `Evidence: ${EVIDENCE_TYPE_LABEL[evidenceType] ?? evidenceType}`
      : "Evidence provided"
    : "No evidence — assumption state";

  return (
    <span
      role="status"
      title={title}
      aria-label={label}
      className={[
        "inline-flex items-center font-medium border rounded",
        "font-['Inter','DM_Sans',sans-serif]",
        sizeClass,
        colorClass,
        className,
      ].join(" ")}
    >
      {isKnow ? (
        <LockIcon className={size === "sm" ? "w-2.5 h-2.5" : "w-3 h-3"} />
      ) : (
        <QuestionIcon className={size === "sm" ? "w-2.5 h-2.5" : "w-3 h-3"} />
      )}
      {label}
    </span>
  );
}

// ── Inline SVG icons (no external icon lib dependency) ────────────────────────

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="3" y="7" width="10" height="8" rx="1.5" fill="currentColor" opacity="0.3" />
      <path
        d="M5 7V5a3 3 0 0 1 6 0v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function QuestionIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M6.5 6.25a1.5 1.5 0 0 1 3 0c0 .9-.75 1.25-1.5 1.75V9"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <circle cx="8" cy="11" r="0.6" fill="currentColor" />
    </svg>
  );
}
